//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useState, useEffect } from 'react';
import { makeStyles, MenuItem, RadioGroup, FormControlLabel, Radio, Grid } from '@material-ui/core';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import Divider from '@material-ui/core/Divider';
import { useTheme } from '@material-ui/core/styles';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import useMediaQuery from '@material-ui/core/useMediaQuery';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import {
    DATE_POSTED_FILTER_OPTIONS,
    JOB_TYPE_FILTER_OPTIONS,
    FILTER_OPTION_JOB_TYPE_ALL_JOB_TYPES,
    MESSAGE_TITLE_SALARY_RATE_INCOMPLETE,
    MESSAGE_SALARY_RATE_INCOMPLETE,
} from '../../common/constants';
import { getUniqueKey } from '../../common/arrayHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { updateRecentSearches } from '../../common/services/jobCenterServices';
import { getNewJobCenterQuery } from '../../common/queryHelper';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdIconButton from '../../common/elemental/AfitdIconButton';
import AfitdSalaryRateFilter from '../../common/elemental/AfitdSalaryRateFilter';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';

import Menu from '../../common/elemental/AfitdMenu';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import CheckBox from './CheckBox';
import SearchSaveItem from './SearchSaveItem';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _get from 'lodash/get';
import _size from 'lodash/size';
import _filter from 'lodash/filter';
import _isEmpty from 'lodash/isEmpty';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => {
        const filterItemStyles = {
            fontSize: '1rem',
            fontWeight: '500',
            [theme.breakpoints.up('md')]: { marginRight: '15px', maxWidth: '212px' },
            [theme.breakpoints.only('md')]: { marginRight: '4px' },
            [theme.breakpoints.down('sm')]: { background: 'white', borderRadius: 8, padding: 16, margin: 16 },
        };

        return {
            filterBar: {
                // display: 'flex',
                borderRadius: '4px',
                // width: '100%',
                justifyContent: 'flex-start', //'flex-end',
                alignItems: 'center',
                // flexDirection: 'row',
                // flexWrap: 'wrap',
                [theme.breakpoints.down('md')]: { justifyContent: 'center' },
                [theme.breakpoints.up('xl')]: { width: '100%' },
                [theme.breakpoints.only('lg')]: { paddingLeft: 13 },
                [theme.breakpoints.up('md')]: { display: 'flex', flexDirection: 'row', flexWrap: 'wrap' },
            },
            menuItem: {
                // minWidth: '220px',
                // border: '1px solid blue',
                whiteSpace: 'normal',
                '& .MuiCheckbox-root, & .MuiRadio-root': {
                    paddingTop: 0,
                    paddingBottom: 0,
                },
                // [theme.breakpoints.down('sm')]: { paddingLeft: 24 },
            },
            jobTypeMenuItem: {
                whiteSpace: 'nowrap',
                '& .MuiCheckbox-root, & .MuiRadio-root': {
                    paddingTop: 0,
                    paddingBottom: 0,
                },
            },
            radioItem: {
                '& label': {
                    marginBottom: 5,
                },
            },
            dateRadio: {
                '& label': {
                    marginBottom: 5,
                },
                // [theme.breakpoints.down('sm')]: { paddingLeft: 24 },
            },
            salaryRadio: {
                '& label': {
                    marginBottom: 5,
                },
                // [theme.breakpoints.down('sm')]: { paddingLeft: 24 },
            },
            filterItem: {
                ...filterItemStyles,
            },
            '@media (max-width: 800px)': {
                filterBar: {
                    justifyContent: 'flex-start',
                },
            },

            checkboxFilterItem: { ...filterItemStyles, marginLeft: 8 },

            recentSearchDropDown: {
                maxWidth: '200px',
                color: '#000',
                border: 'none',
                outline: 'none',
                boxShadow: 'none',
            },

            clearRecentSearchesButton: {
                width: '100%',
                color: '#1D8A8B',
            },
            resultButton: { width: '100%', marginBottom: 16 },
            resultButtonContainer: { margin: '0px 16px 16px 16px' },
            filterTitle: { fontSize: 16, fontWeight: 'bold', margin: '21px 16px 16px 16px' },
            bottomDivider: {
                marginBottom: 16,
            },
            navigation: { padding: '0px 16px' },
            navigationIconContainer: {
                width: '32px',
                height: '32px',
                background: '#F2F2F4',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                '&:hover': { cursor: 'pointer' },
            },
            navIconLeft: { marginLeft: 11, width: 18 },
            navigationText: { marginLeft: 8, fontSize: 16, fontWeight: 'bold', '&:hover': { cursor: 'pointer' }, marginTop: '-2px' },
            filterHeaderContainer2: { display: 'flex', justifyContent: 'center' },
            filterHeaderContainer3: { display: 'flex', justifyContent: 'flex-end' },
            filterHeaderText: { fontSize: '15px', fontWeight: 600 },
            resetText: { fontSize: '15px', fontWeight: 600, cursor: 'pointer' },
            headerDivider: { paddingTop: 16 },
            salaryDropDown: {
                border: '1px solid green',
            },
            IconButtonContainer: {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-end',
                marginTop: '5px',
            },
            clearFilterButton: {
                padding: '5px',
            },
            closeIcon: {
                color: 'rgba(0, 0, 0, 0.54)',
                fontSize: '18px',
            },
            submitFilterButton: {
                padding: '5px',
                marginRight: '10px',
            },
            checkIcon: {
                color: 'rgba(0, 0, 0, 0.54)',
                fontSize: '18px',
            },
        };
    });
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        appContext,
        smMatches,
        // isDirty,
        // setIsDirty,
        // currency,
        // setCurrency,
        salaryRateIsValid,
        setSalaryRateIsValid,
        smViewportSalaryRate,
        handleSubmitSalaryRate,
        setIsMenuClosed,
        datePost,
        workFromHome,
        internPositions,
        handleChangeDatePost,
        handleChangeWorkFromHome,
        handleChangeInternPositions,
        handleChangeStandaloneWorkFromHome,
        handleChangeStandaloneInternPositions,
        handleClearSalaryRate,
        salaryRateIncompleteMessageDialogOpen,
        handleCloseSalaryRateIncompleteMessageDialogOpen,
        handleClickReset,
    } = params;

    // console.log('params', rateMin, rateMax);
    const { query = {}, setQuery } = props;
    const salaryRateInQuery = _get(query, 'salaryRate');
    const isExactRateInQuery = _get(salaryRateInQuery, 'isExactRate', null);
    const rateMinInQuery = _get(salaryRateInQuery, 'rateMin', '');
    const rateMaxInQuery = _get(salaryRateInQuery, 'rateMax', '');
    const payFrequencyInQuery = _get(salaryRateInQuery, 'payFrequency', null);
    // const currencySymbol = _get(salaryRateInQuery, 'currencySymbol', null);
    const workFromHomeInQuery = _get(query, 'workFromHome');
    const internPositionsInQuery = _get(query, 'internPositions');

    const isExactRateSmViewport = _get(smViewportSalaryRate, 'isExactRate', null);
    const rateMinSmViewport = _get(smViewportSalaryRate, 'rateMin', '');
    const rateMaxSmViewport = _get(smViewportSalaryRate, 'rateMax', '');
    const payFrequencySmViewport = _get(smViewportSalaryRate, 'payFrequency', null);
    // const currencySymbolSmViewport = _get(smViewportSalaryRate, 'currencySymbol', null);

    const { userId } = appContext;

    const defns = {
        navigation: { container: true, alignItems: 'center' },
        navigationItem: { item: true, xl: 4, lg: 4, md: 4, sm: 4, xs: 4 },
        filterHeaderContainer2: { item: true, xl: 4, lg: 4, md: 4, sm: 4, xs: 4 },
        filterHeaderContainer3: { item: true, xl: 4, lg: 4, md: 4, sm: 4, xs: 4 },
        recentSearchDropDown: {
            closeable: true,
            label: 'Recent searches',
            name: 'recentSearches',
        },
        jobTypeDropDown: {
            label: 'Job Type',
            name: 'jobTypes',
            onClick: () => setIsMenuClosed(false),
        },
        dateDropDown: {
            label: 'Date Posted',
            name: 'datePost',
            onClick: () => setIsMenuClosed(false),
        },
        dateRadio: {
            value: datePost,
            'aria-label': 'date',
            name: 'date',
            onChange: handleChangeDatePost,
        },
        salaryRadio: {
            value: query.salary,
            'aria-label': 'salary',
            name: 'salary',
            onChange: e => {
                let val = e.target.value;
                // setQuery(prevQuery => getNewJobCenterQuery({ prevQuery, newQuery: { xxxxxxxxxx } }));
                setQuery(prevQuery => getNewJobCenterQuery({ prevQuery, newQuery: { salary: val } }));
            },
        },
        salaryDropDown: {
            label: 'Desired Salary / Rate',
            name: 'salary',
            onClick: () => setIsMenuClosed(false),
        },
        salaryRate: {
            variant: 'single',
            isExactRate: smMatches ? isExactRateSmViewport : isExactRateInQuery,
            rateMin: smMatches ? rateMinSmViewport : rateMinInQuery,
            rateMax: smMatches ? rateMaxSmViewport : rateMaxInQuery,
            payFrequency: smMatches ? payFrequencySmViewport : payFrequencyInQuery,
            onSubmit: handleSubmitSalaryRate,
            onClear: handleClearSalaryRate,
            maxValue: MAX_VALUE,
            setIsValid: setSalaryRateIsValid,
            isValid: salaryRateIsValid,
        },
        workFromHome: {
            label: 'Work from Home',
            onChange: handleChangeWorkFromHome,
            checked: Boolean(workFromHome),
        },
        internPositions: {
            label: 'Intern Positions',
            onChange: handleChangeInternPositions,
            checked: Boolean(internPositions),
        },
        standaloneWorkFromHome: {
            label: 'Work from Home',
            onChange: handleChangeStandaloneWorkFromHome,
            checked: Boolean(workFromHomeInQuery),
        },
        standaloneInternPositions: {
            label: 'Intern Positions',
            onChange: handleChangeStandaloneInternPositions,
            checked: Boolean(internPositionsInQuery),
        },
        clearFilterButton: {
            variant: 'text',
            color: 'primary',
        },
        recentSearchPlaceholder: {
            style: {
                textAlign: 'center',
            },
            color: 'secondary',
            variant: 'subtitle1',
        },
        clearRecentSearchesButton: {
            onClick: () => {
                updateRecentSearches({ userId, recentSearches: [] });
            },
        },
        resultButton: { color: 'primary' },
        resetText: {
            onClick: handleClickReset,
        },
        salaryRateIncompleteMessageDialog: {
            title: MESSAGE_TITLE_SALARY_RATE_INCOMPLETE,
            open: salaryRateIncompleteMessageDialogOpen,
            message: MESSAGE_SALARY_RATE_INCOMPLETE,
            onClose: handleCloseSalaryRateIncompleteMessageDialogOpen,
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const MAX_VALUE = 1000000;

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const FilterBar = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();
    const appContext = useContext(AppContext);
    // const { userId } = appContext;

    const layoutContext = useContext(LayoutContext);
    const { recentSearches } = layoutContext;

    const theme = useTheme();
    const xlMatches = useMediaQuery(theme.breakpoints.only('xl'));
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { query, setQuery, setShowFilterPage, setShowAds, queryInitState } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [isDirty, setIsDirty] = useState(false);
    const [currency, setCurrency] = useState(null);
    const [salaryRateIsValid, setSalaryRateIsValid] = useState(true);
    const [selectedJobTypes, setSelectedJobTypes] = useState([]);
    const [isMenuClosed, setIsMenuClosed] = useState(false);
    const [datePost, setDatePost] = useState();
    const [workFromHome, setWorkFromHome] = useState();
    const [internPositions, setInternPositions] = useState();
    const [smViewportSalaryRate, setSmViewportSalaryRate] = useState(null);
    const [salaryRateIncompleteMessageDialogOpen, setSalaryRateIncompleteMessageDialogOpen] = useState(false);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        const jobTypesInQuery = query?.jobType;
        const datePostInQuery = query?.datePost;
        const workFromHomeInQuery = query?.workFromHome;
        const internPositionsInQuery = query?.internPositions;
        const salaryRateInQuery = query?.salaryRate;

        // sync job types
        // if not all selected, remove 'All Job Types'; otherwise, sync query with checkboxes
        if (_size(JOB_TYPE_FILTER_OPTIONS) !== _size(jobTypesInQuery)) setSelectedJobTypes(_filter(jobTypesInQuery, el => el !== FILTER_OPTION_JOB_TYPE_ALL_JOB_TYPES));
        else setSelectedJobTypes(jobTypesInQuery);

        // sync date posted
        setDatePost(datePostInQuery);

        // sync work from home
        setWorkFromHome(workFromHomeInQuery);

        // sync work from home
        setInternPositions(internPositionsInQuery);

        // sync salary/rate (for 360px viewport)
        setSmViewportSalaryRate(salaryRateInQuery);
    }, [query]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    // handle salaryRate onSubmit
    const handleSubmitSalaryRate = ({ isExactRate, rateMin, rateMax, payFrequency, currencySymbol, showOnly } = {}) => {
        if (!rateMin && !payFrequency) setQuery(prevQuery => getNewJobCenterQuery({ prevQuery, newQuery: { salaryRate: null } }));
        else
            setQuery(prevQuery =>
                getNewJobCenterQuery({ prevQuery, newQuery: { salaryRate: { rateMin: rateMin || 0, rateMax, payFrequency, currencySymbol, showOnly /* isExactRate */ } } }),
            );
        setIsMenuClosed(true);
    };

    // handle salaryRate onSubmit on 360px viewport (onChange events)
    const handleChangeSalaryRate = ({ isExactRate, rateMin, rateMax, payFrequency, currencySymbol, showOnly } = {}) => {
        if ((rateMin && payFrequency) || (!rateMin && !payFrequency)) {
            setSalaryRateIncompleteMessageDialogOpen(false);
            setSalaryRateIsValid(true);
        }

        setSmViewportSalaryRate(prev => ({
            ...prev,
            rateMin,
            rateMax,
            payFrequency,
            showOnly,
        }));
    };

    // handle salaryRate onClear
    const handleClearSalaryRate = () => {
        setQuery(prevQuery => getNewJobCenterQuery({ prevQuery, newQuery: { salaryRate: null } }));
        setSalaryRateIsValid(true);
        setIsMenuClosed(true);
    };

    const handleChangeJobType = val => {
        // if already in the list, user is deselecting
        const isDeselect = selectedJobTypes.includes(val);

        // if 'All Job Types'
        if (val === FILTER_OPTION_JOB_TYPE_ALL_JOB_TYPES) setSelectedJobTypes(isDeselect ? [] : JOB_TYPE_FILTER_OPTIONS);
        else {
            // individual job type (if deselecting, remove that along 'All Job Types')
            isDeselect
                ? setSelectedJobTypes(_filter(selectedJobTypes, el => el !== val && el !== FILTER_OPTION_JOB_TYPE_ALL_JOB_TYPES))
                : setSelectedJobTypes([...selectedJobTypes, val]);
        }
    };

    // handle date posted onChange
    const handleChangeDatePost = e => {
        let val = e.target.value;
        setDatePost(val);
    };

    // handle work from home onChange
    const handleChangeWorkFromHome = val => {
        setWorkFromHome(!!val);
    };

    // handle work from home onChange
    const handleChangeInternPositions = val => {
        setInternPositions(!!val);
    };

    // query on job type
    const handleSubmitJobType = () => {
        setQuery(prevQuery => getNewJobCenterQuery({ prevQuery, newQuery: { jobType: selectedJobTypes } }));
        setIsMenuClosed(true);
    };

    // clearing out job types
    const handleClearJobType = () => {
        setSelectedJobTypes([]);
        setQuery(prevQuery => getNewJobCenterQuery({ prevQuery, newQuery: { jobType: [] } }));
        setIsMenuClosed(true);
    };

    // query in phone screen (360px viewport)
    const handleClickShowResultButton = async () => {
        // validate Salary/Rate
        const rateMin = smViewportSalaryRate?.rateMin;
        // const rateMax = smViewportSalaryRate?.rateMax;
        const payFrequency = smViewportSalaryRate?.payFrequency;
        // const showOnly = smViewportSalaryRate?.showOnly;
        if ((rateMin && !payFrequency) || (!rateMin && payFrequency)) {
            setSalaryRateIncompleteMessageDialogOpen(true);
            setSalaryRateIsValid(false);
        } else {
            setSalaryRateIsValid(true);

            const queryDto = { jobType: selectedJobTypes, datePost, workFromHome: true, internPositions: true, salaryRate: smViewportSalaryRate };

            setQuery(prevQuery => {
                const omitFilters = [];
                if (!workFromHome) omitFilters.push('workFromHome');
                if (!internPositions) omitFilters.push('internPositions');
                const omitDto = _isEmpty(omitFilters) ? {} : { omit: omitFilters };
                const newQuery = getNewJobCenterQuery({ prevQuery, newQuery: queryDto, ...omitDto });
                return newQuery;
            });
            setShowAds(false);
            setShowFilterPage(false);
        }
    };

    // query on date posted
    const handleSubmitDatePost = () => {
        setQuery(prevQuery => getNewJobCenterQuery({ prevQuery, newQuery: { datePost } }));
        setIsMenuClosed(true);
    };

    // clearing out date posted
    const handleClearDatePost = () => {
        setDatePost(null);
        setQuery(prevQuery => getNewJobCenterQuery({ prevQuery, newQuery: { ...prevQuery }, omit: ['datePost'] }));
        setIsMenuClosed(true);
    };

    // handle stand-alone work from home, which is ready to submit query
    const handleChangeStandaloneWorkFromHome = val => {
        setQuery(prevQuery =>
            prevQuery?.workFromHome
                ? getNewJobCenterQuery({ prevQuery, newQuery: { ...prevQuery, workFromHome: false } })
                : getNewJobCenterQuery({ prevQuery, newQuery: { workFromHome: true } }),
        );
    };

    // handle stand-alone intern positions, which is ready to submit query
    const handleChangeStandaloneInternPositions = val => {
        setQuery(prevQuery =>
            prevQuery?.internPositions
                ? getNewJobCenterQuery({ prevQuery, newQuery: { ...prevQuery, internPositions: false } })
                : getNewJobCenterQuery({ prevQuery, newQuery: { internPositions: true } }),
        );
    };

    const handleCloseSalaryRateIncompleteMessageDialogOpen = () => {
        setSalaryRateIncompleteMessageDialogOpen(false);
    };

    const handleClickReset = () => {
        setSalaryRateIsValid(true);
        setSmViewportSalaryRate(null);
        setQuery(queryInitState);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        appContext,
        smMatches,
        isDirty,
        setIsDirty,
        currency,
        setCurrency,
        salaryRateIsValid,
        setSalaryRateIsValid,
        smViewportSalaryRate,
        handleSubmitSalaryRate,
        setIsMenuClosed,
        datePost,
        workFromHome,
        internPositions,
        handleChangeDatePost,
        handleChangeWorkFromHome,
        handleChangeInternPositions,
        handleChangeStandaloneWorkFromHome,
        handleChangeStandaloneInternPositions,
        handleClearSalaryRate,
        salaryRateIncompleteMessageDialogOpen,
        handleCloseSalaryRateIncompleteMessageDialogOpen,
        handleClickReset,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <>
            {smMatches ? (
                <div {...defns.filterBar}>
                    <Grid {...defns.navigation}>
                        <Grid {...defns.navigationItem}>
                            <div {...defns.navigationIconContainer} onClick={() => setShowFilterPage(false)}>
                                <ArrowBackIosIcon {...defns.navIconLeft} />
                            </div>
                        </Grid>
                        <Grid {...defns.filterHeaderContainer2}>
                            <p {...defns.filterHeaderText}>Filter</p>
                        </Grid>
                        <Grid {...defns.filterHeaderContainer3}>
                            <p {...defns.resetText} style={{ cursor: 'pointer' }}>
                                Reset
                            </p>
                        </Grid>
                    </Grid>
                    <div {...defns.headerDivider}>
                        <Divider />
                    </div>
                    <p {...defns.filterTitle}>Job Type</p>
                    <div {...defns.filterItem}>
                        {JOB_TYPE_FILTER_OPTIONS.map(el => (
                            <p {...defns.menuItem}>
                                <CheckBox label={el} checked={selectedJobTypes.includes(el)} onChange={handleChangeJobType} />
                            </p>
                        ))}
                    </div>
                    <p {...defns.filterTitle}>Date Posted</p>
                    <div {...defns.filterItem}>
                        <RadioGroup {...defns.dateRadio}>
                            {DATE_POSTED_FILTER_OPTIONS.map(el => {
                                let [prefix, ...rest] = el.split(' ');
                                return (
                                    <FormControlLabel
                                        value={el}
                                        control={<Radio disableRipple />}
                                        label={
                                            <span>
                                                <i>{`${prefix} `}</i>
                                                {rest.join(' ')}
                                            </span>
                                        }
                                    />
                                );
                            })}
                        </RadioGroup>
                    </div>
                    <p {...defns.filterTitle}>Desired Salary / Rate</p>
                    <div {...defns.filterItem}>
                        <AfitdSalaryRateFilter {...defns.salaryRate} isButtonHidden={true} onSubmit={handleChangeSalaryRate} />
                    </div>
                    <div {...defns.filterItem} style={{ padding: '8px 8px 8px 16px' }}>
                        <CheckBox {...defns.workFromHome} />
                    </div>
                    <div {...defns.filterItem} style={{ padding: '8px 8px 8px 16px' }}>
                        <CheckBox {...defns.internPositions} />
                    </div>
                    <div {...defns.bottomDivider}>
                        <Divider />
                    </div>
                    <div {...defns.resultButtonContainer}>
                        <AfitdButton {...defns.resultButton} onClick={handleClickShowResultButton}>
                            Show Results
                        </AfitdButton>
                    </div>
                </div>
            ) : (
                <div {...defns.filterBar}>
                    {!xlMatches && (
                        <div {...defns.filterItem}>
                            <Menu {...defns.recentSearchDropDown}>
                                {recentSearches.map(el => (
                                    <MenuItem {...defns.menuItem} key={getUniqueKey()}>
                                        <SearchSaveItem onClick={() => setQuery(el)} {...el} />
                                    </MenuItem>
                                ))}
                                {recentSearches.length ? (
                                    <AfitdButton {...defns.clearRecentSearchesButton}>Clear</AfitdButton>
                                ) : (
                                    <AfitdTypography {...defns.recentSearchPlaceholder}>No Recent Searches</AfitdTypography>
                                )}
                            </Menu>
                        </div>
                    )}

                    <div {...defns.filterItem}>
                        <Menu {...defns.jobTypeDropDown} close={isMenuClosed}>
                            {JOB_TYPE_FILTER_OPTIONS.map(el => (
                                <MenuItem {...defns.jobTypeMenuItem}>
                                    <CheckBox label={el} checked={selectedJobTypes.includes(el)} onChange={handleChangeJobType} />
                                </MenuItem>
                            ))}
                            <div {...defns.IconButtonContainer}>
                                <AfitdIconButton {...defns.submitFilterButton} onClick={handleSubmitJobType}>
                                    <CheckIcon {...defns.checkIcon} />
                                </AfitdIconButton>
                                <AfitdIconButton {...defns.clearFilterButton} onClick={handleClearJobType}>
                                    <CloseIcon {...defns.closeIcon} />
                                </AfitdIconButton>
                            </div>
                        </Menu>
                    </div>
                    <div {...defns.filterItem}>
                        <Menu {...defns.dateDropDown} close={isMenuClosed}>
                            <RadioGroup {...defns.dateRadio}>
                                {DATE_POSTED_FILTER_OPTIONS.map(el => {
                                    let [prefix, ...rest] = el.split(' ');
                                    return (
                                        <MenuItem {...defns.menuItem}>
                                            <FormControlLabel
                                                value={el}
                                                control={<Radio disableRipple />}
                                                label={
                                                    <span>
                                                        <i>{`${prefix} `}</i>
                                                        {rest.join(' ')}
                                                    </span>
                                                }
                                            />
                                        </MenuItem>
                                    );
                                })}
                            </RadioGroup>
                            <div {...defns.IconButtonContainer}>
                                <AfitdIconButton {...defns.submitFilterButton} onClick={handleSubmitDatePost}>
                                    <CheckIcon {...defns.checkIcon} />
                                </AfitdIconButton>
                                <AfitdIconButton {...defns.clearFilterButton} onClick={handleClearDatePost}>
                                    <CloseIcon {...defns.closeIcon} />
                                </AfitdIconButton>
                            </div>
                        </Menu>
                    </div>
                    <div {...defns.filterItem}>
                        <Menu {...defns.salaryDropDown} close={isMenuClosed}>
                            <AfitdSalaryRateFilter {...defns.salaryRate} />
                        </Menu>
                    </div>
                    <div {...defns.checkboxFilterItem}>
                        <CheckBox {...defns.standaloneWorkFromHome} />
                    </div>
                    <div {...defns.checkboxFilterItem}>
                        <CheckBox {...defns.standaloneInternPositions} />
                    </div>
                </div>
            )}
            <AfitdMessageDialog {...defns.salaryRateIncompleteMessageDialog} />
        </>
    );
};

export default FilterBar;
